import { fetchWithAuth } from "../../api-client/api_client";

export interface WriteResult<T = unknown> {
  ok: boolean;
  status?: number;
  // The created/updated entity returned by conductor (e.g. to read a new id).
  data?: T;
  // Conductor's own message, e.g. why a precondition failed.
  error?: string;
}

// The backend wraps a conductor gRPC error detail as {"non_field_errors": [...]}.
const readErrorDetail = async (response: Response | undefined): Promise<string | undefined> => {
  if (!response) return undefined;
  try {
    const body = await response.json();
    const detail = body?.non_field_errors?.[0] ?? body?.detail;
    return typeof detail === "string" ? detail : undefined;
  } catch {
    return undefined;
  }
};

// POST a conductor entity (create or update). The body is the entity's JSON
// shape (bare-string UUIDs, full enum names, dict metadata) the backend app
// models expect. Resolves { ok, data }; callers decide how to surface
// success/error (e.g. one summary toast for a batch of writes).
export function conductorPost<T = unknown>(path: string, body: unknown): Promise<WriteResult<T>> {
  return new Promise((resolve) => {
    fetchWithAuth({
      path,
      method: "POST",
      body,
      onResponse: (response: T) => resolve({ ok: true, data: response }),
      onError: (response) =>
        readErrorDetail(response).then((error) =>
          resolve({ ok: false, status: response?.status, error })
        ),
    });
  });
}

// PUT a conductor entity. Used by flags, where update is a full replacement
// rather than the create-or-update upsert the other entities use, so the body
// must carry the whole object.
export function conductorPut<T = unknown>(path: string, body: unknown): Promise<WriteResult<T>> {
  return new Promise((resolve) => {
    fetchWithAuth({
      path,
      method: "PUT",
      body,
      onResponse: (response: T) => resolve({ ok: true, data: response }),
      onError: (response) =>
        readErrorDetail(response).then((error) =>
          resolve({ ok: false, status: response?.status, error })
        ),
    });
  });
}

// DELETE that returns an entity rather than 204. Clearing a sync speed override
// answers with the reverted row, which carries the speed the account fell back
// to, so the body has to be read rather than discarded.
export function conductorDeleteJson<T = unknown>(path: string): Promise<WriteResult<T>> {
  return new Promise((resolve) => {
    fetchWithAuth({
      path,
      method: "DELETE",
      onResponse: (response: T) => resolve({ ok: true, data: response }),
      onError: (response) =>
        readErrorDetail(response).then((error) =>
          resolve({ ok: false, status: response?.status, error })
        ),
    });
  });
}

// DELETE a conductor entity by path. Conductor rejects deleting a plan that is
// still referenced (HTTP 409); the status is surfaced so callers can explain.
export function conductorDelete(path: string): Promise<WriteResult> {
  return new Promise((resolve) => {
    fetchWithAuth({
      path,
      method: "DELETE",
      expectJSON: false,
      onResponse: () => resolve({ ok: true }),
      onError: (response) =>
        readErrorDetail(response).then((error) =>
          resolve({ ok: false, status: response?.status, error })
        ),
    });
  });
}
